import { supabase }  from '../supabase.js';
import { pageQuery } from '../lib/egress.js';
import { roleName }  from '../lib/admin-words.js';

/* ============================================================
   People & rights (docs/admin-area.md) — the admin side's
   version of Users: a ROLE per person and the PARTS of the
   dashboard they may use, switched one by one. Every change
   goes through grant_user_role / revoke_user_role and is
   recorded by the audit triggers on user_roles.

   There is no delete here, on purpose: revoking keeps the
   sign-in and the history; the row simply loses its role.
============================================================ */

// The ladder (SME-Connect bible/roles-standard.md), highest first.
const ROLES = ['owner', 'admin', 'manager', 'facilitator', 'readonly'];
const TOP_ROLES = ['owner', 'admin'];   // only an Owner gives, changes or removes these

const ROLE_HELP = {
  owner: 'Everything an admin has, and alone gives or removes the Admin and Owner roles.',
  admin: 'Everything: every Work page and record, every part, and this admin side — may change who has access below Admin.',
  manager: 'Sees every Work page and record; changes what their areas allow (all on by default). Staff HR only when switched on.',
  facilitator: 'Their own classes and learners: marks attendance, sees their learners’ pay.',
  readonly: 'Sees every Work page and record; changes nothing until an area is switched on.',
};

let _state = { filter: 'with-role', q: '' };

export async function renderAdminPeople() {
  const el = document.getElementById('view-admin-people');
  const access = window.__access;
  if (!access?.admin) {
    el.innerHTML = `<div class="page-header"><h1>People &amp; rights</h1><p>Admins only.</p></div>`;
    return;
  }

  el.innerHTML = `
    <div class="page-header page-header-row">
      <div>
        <h1>People &amp; rights</h1>
        <p>Everyone who has signed in, their role, and the parts of the dashboard they may use. A role is the default bundle; a switch opens one part for one person, on any role. An admin has every part. Revoking keeps their history.</p>
      </div>
      <button type="button" class="btn btn-primary" id="ap-add-toggle" aria-expanded="false">Pre-create a facilitator</button>
    </div>

    <div id="ap-msg" class="cp-msg" role="status"></div>

    <div class="table-card hidden" id="ap-add-card" style="padding:18px 20px">
      <div class="section-title">Pre-create a facilitator</div>
      <form id="add-fac-form" class="users-form">
        <label>Full name <input type="text"  id="af-name"  required /></label>
        <label>Email     <input type="email" id="af-email" required /></label>
        <label>Cell      <input type="tel"   id="af-cell"  placeholder="e.g. 0735146880" /></label>
        <button type="submit" class="btn btn-primary" id="af-submit">Create facilitator</button>
        <div id="af-msg" class="cp-msg"></div>
      </form>
      <p class="users-help">
        Pre-creating lets you assign them to classes before they sign in. When they sign in with that Google
        address they get the facilitator role automatically; otherwise link them below.
      </p>
    </div>

    <div class="filter-row ap-filters">
      <button type="button" class="filter-btn" data-filter="with-role">With access</button>
      <button type="button" class="filter-btn" data-filter="no-role">Signed in, no role</button>
      <button type="button" class="filter-btn" data-filter="all">Everyone</button>
      <input type="search" id="ap-search" class="select-input" placeholder="Find a person…" aria-label="Find a person" value="${esc(_state.q)}" />
      <span class="ap-count" id="ap-count"></span>
    </div>

    <div class="table-card">
      <div class="table-wrap">
        <table class="data-table ap-table">
          <thead><tr id="ap-head"></tr></thead>
          <tbody id="ap-tbody"><tr><td colspan="5" class="no-data">Loading…</td></tr></tbody>
        </table>
      </div>
    </div>
    <p class="users-help">
      <strong>Live now.</strong> Role, linked facilitator and every switch take effect on the person’s next page load.
      Nobody changes their own access — ask another admin.
      Sign-in is Google or email and password; a person who has never signed in does not appear here yet.
    </p>
  `;

  el.querySelector('#ap-add-toggle').addEventListener('click', (e) => {
    const card = el.querySelector('#ap-add-card');
    const open = card.classList.toggle('hidden');
    e.currentTarget.setAttribute('aria-expanded', String(!open));
    e.currentTarget.textContent = open ? 'Pre-create a facilitator' : 'Close';
  });

  el.querySelector('#add-fac-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = el.querySelector('#af-submit');
    const msg = el.querySelector('#af-msg');
    msg.className = 'cp-msg'; msg.textContent = '';
    btn.disabled = true; btn.textContent = 'Creating…';
    const { error } = await supabase.rpc('add_facilitator', {
      p_full_name: el.querySelector('#af-name').value.trim(),
      p_email: el.querySelector('#af-email').value.trim(),
      p_cell_number: el.querySelector('#af-cell').value.trim() || null,
    });
    btn.disabled = false; btn.textContent = 'Create facilitator';
    if (error) { msg.className = 'cp-msg error'; msg.textContent = error.message; return; }
    msg.className = 'cp-msg success'; msg.textContent = 'Facilitator created.';
    e.target.reset();
    await reload();
  });

  el.querySelectorAll('.ap-filters .filter-btn').forEach((b) => {
    b.classList.toggle('active', b.dataset.filter === _state.filter);
    b.setAttribute('aria-pressed', String(b.dataset.filter === _state.filter));
    b.addEventListener('click', () => { _state.filter = b.dataset.filter; renderAdminPeople(); });
  });
  el.querySelector('#ap-search').addEventListener('input', (e) => { _state.q = e.target.value; drawRows(); });

  await reload();
}

let _users = [];
let _facs = [];
let _parts = [];   // access_parts: the switch columns, in order

const colspan = () => 5 + _parts.length;

async function reload() {
  const tbody = document.getElementById('ap-tbody');
  if (!tbody) return;
  tbody.innerHTML = `<tr><td colspan="${colspan()}" class="no-data">Loading…</td></tr>`;
  const [{ data: users, error }, { data: facs }, { data: parts }] = await Promise.all([
    supabase.rpc('list_users_with_roles'),
    pageQuery('facilitators', { order: 'full_name', ascending: true, select: 'facilitator_id,full_name' }),
    supabase.from('access_parts').select('key,label,help,every_record').order('sort'),
  ]);
  _parts = parts ?? [];
  const head = document.getElementById('ap-head');
  if (head) head.innerHTML = `
    <th>Person</th><th>Role</th><th>Linked facilitator</th>
    ${_parts.map((p) => `<th class="ap-switch-head" title="${esc(p.help)}">${esc(p.label)}</th>`).join('')}
    <th>Last sign-in</th><th></th>`;
  if (error) { tbody.innerHTML = `<tr><td colspan="${colspan()}" class="no-data">${esc(error.message)}</td></tr>`; return; }
  _users = users ?? [];
  _facs = facs ?? [];
  drawRows();
}

function drawRows() {
  const tbody = document.getElementById('ap-tbody');
  const count = document.getElementById('ap-count');
  if (!tbody) return;
  const me = window.__userEmail;
  const iAmOwner = window.__access?.owner === true;
  const needle = _state.q.trim().toLowerCase();

  const shown = _users.filter((u) => {
    if (_state.filter === 'with-role' && !u.role) return false;
    if (_state.filter === 'no-role' && u.role) return false;
    if (needle && !`${u.display_name ?? ''} ${u.email ?? ''} ${u.role ?? ''}`.toLowerCase().includes(needle)) return false;
    return true;
  }).sort((a, b) => (ROLES.indexOf(a.role ?? 'zz') - ROLES.indexOf(b.role ?? 'zz')) || String(a.display_name ?? a.email).localeCompare(String(b.display_name ?? b.email)));

  if (count) count.textContent = `${shown.length} shown`;

  if (!shown.length) {
    tbody.innerHTML = `<tr><td colspan="${colspan()}" class="no-data">${_users.length ? 'Nobody matches. Clear the search or pick another filter.' : 'Nobody has signed in yet.'}</td></tr>`;
    return;
  }

  const facOptions = (selectedId) => `
    <option value="">— pick facilitator —</option>
    ${_facs.map((f) => `<option value="${f.facilitator_id}" ${f.facilitator_id === selectedId ? 'selected' : ''}>${esc(f.full_name)}</option>`).join('')}`;

  tbody.innerHTML = shown.map((u) => {
    const isSelf = (u.email ?? '').toLowerCase() === (me ?? '').toLowerCase();
    // An admin leaves Owner and Admin rows alone; nobody changes their own row.
    const locked = isSelf || (TOP_ROLES.includes(u.role) && !iAmOwner);
    const lockedWhy = isSelf ? 'You cannot change your own access.' : 'Only an Owner can change an Admin or Owner.';
    const lastSeen = u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : 'never';
    const nameCell = `
      <strong>${esc(u.display_name ?? u.email ?? '—')}</strong>
      ${isSelf ? '<span class="badge badge-orange ap-badge">you</span>' : ''}
      <div class="ap-email">${esc(u.email ?? '')}</div>`;
    const roleSel = `
      <select class="select-input u-role" data-email="${esc(u.email)}" ${locked ? 'disabled' : ''} aria-label="Role for ${esc(u.display_name ?? u.email)}" title="${esc(locked ? lockedWhy : ROLE_HELP[u.role] ?? 'No role: cannot open the dashboard.')}">
        <option value="">no access</option>
        ${ROLES.map((r) => `<option value="${r}" ${u.role === r ? 'selected' : ''} ${TOP_ROLES.includes(r) && !iAmOwner ? 'disabled' : ''}>${roleName(r)}</option>`).join('')}
      </select>`;
    const facSel = `
      <select class="select-input u-fac" data-email="${esc(u.email)}" ${u.role === 'facilitator' && !locked ? '' : 'disabled'} aria-label="Facilitator record for ${esc(u.display_name ?? u.email)}">
        ${facOptions(u.facilitator_id)}
      </select>`;
    const has = u.parts ?? [];
    const switches = _parts.map((p) => {
      const byRole = TOP_ROLES.includes(u.role);
      const ownClasses = u.role === 'facilitator' && p.every_record;
      const off = locked || byRole || ownClasses || !u.role;
      const title = locked ? lockedWhy
        : byRole ? `${roleName(u.role)} has every part.`
        : ownClasses ? 'A facilitator changes their own classes’ records; this switch is for every class.'
        : !u.role ? 'Give a role first.' : p.help;
      return `<td class="ap-switch">
        <input type="checkbox" class="u-switch" data-email="${esc(u.email)}" data-key="${esc(p.key)}"
          ${byRole || has.includes(p.key) ? 'checked' : ''} ${off ? 'disabled' : ''}
          aria-label="${esc(p.label)} for ${esc(u.display_name ?? u.email)}" title="${esc(title)}" />
      </td>`;
    }).join('');
    const actions = u.role && !locked
      ? `<button type="button" class="btn btn-ghost u-revoke" data-user-id="${u.user_id}" data-name="${esc(u.display_name ?? u.email)}">Revoke access</button>`
      : '';
    const reset = `<button type="button" class="btn btn-ghost u-reset" data-email="${esc(u.email)}">Reset password</button>`;
    return `<tr>
      <td>${nameCell}</td>
      <td>${roleSel}</td>
      <td>${facSel}</td>
      ${switches}
      <td class="ap-muted">${lastSeen}</td>
      <td class="ap-actions">${actions}${reset}</td>
    </tr>`;
  }).join('');

  tbody.querySelectorAll('.u-role').forEach((sel) => sel.addEventListener('change', async () => {
    const u = _users.find((x) => x.email === sel.dataset.email);
    const role = sel.value;
    if (!role) { note('To remove access, use Revoke access.', true); drawRows(); return; }
    const facSelEl = tbody.querySelector(`.u-fac[data-email="${cssEsc(sel.dataset.email)}"]`);
    facSelEl.disabled = role !== 'facilitator';
    const facId = role === 'facilitator' ? facSelEl.value || null : null;
    if (role === 'facilitator' && !facId) { note('Pick a facilitator record in the next column first.', true); facSelEl.focus(); return; }
    await grant(u, { p_role: role, p_facilitator_id: facId });
  }));

  tbody.querySelectorAll('.u-fac').forEach((sel) => sel.addEventListener('change', async () => {
    const u = _users.find((x) => x.email === sel.dataset.email);
    const roleSelEl = tbody.querySelector(`.u-role[data-email="${cssEsc(sel.dataset.email)}"]`);
    if (roleSelEl.value !== 'facilitator' || !sel.value) return;
    await grant(u, { p_role: 'facilitator', p_facilitator_id: sel.value });
  }));

  tbody.querySelectorAll('.u-switch').forEach((cb) => cb.addEventListener('change', async () => {
    const u = _users.find((x) => x.email === cb.dataset.email);
    const key = cb.dataset.key;
    const parts = (u.parts ?? []).filter((k) => k !== key).concat(cb.checked ? [key] : []);
    cb.disabled = true;
    await grant(u, { p_role: u.role, p_facilitator_id: u.facilitator_id, p_parts: parts });
  }));

  tbody.querySelectorAll('.u-revoke').forEach((btn) => btn.addEventListener('click', async () => {
    if (!confirm(`Revoke ${btn.dataset.name}’s access? They keep their sign-in and their history, and can be granted a role again later.`)) return;
    btn.disabled = true; btn.textContent = 'Revoking…';
    const { error } = await supabase.rpc('revoke_user_role', { p_user_id: btn.dataset.userId });
    if (error) { note(error.message, true); btn.disabled = false; btn.textContent = 'Revoke access'; return; }
    note(`${btn.dataset.name}’s access revoked.`);
    await reload();
  }));

  tbody.querySelectorAll('.u-reset').forEach((btn) => btn.addEventListener('click', async () => {
    const email = btn.dataset.email;
    if (!confirm(`Send a password reset email to ${email}?`)) return;
    btn.disabled = true; btn.textContent = 'Sending…';
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    if (error) { note('Could not send the reset email: ' + error.message, true); btn.disabled = false; btn.textContent = 'Reset password'; return; }
    btn.textContent = 'Sent ✓';
  }));
}

async function grant(u, args) {
  const { error } = await supabase.rpc('grant_user_role', { p_email: u.email, ...args });
  if (error) note(error.message, true);
  else note(`Saved for ${u.display_name ?? u.email}.`);
  await reload();
}

function note(text, isError = false) {
  const m = document.getElementById('ap-msg');
  if (!m) return;
  m.className = 'cp-msg ' + (isError ? 'error' : 'success');
  m.textContent = text;
}

function esc(s)    { return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
function cssEsc(s) { return String(s ?? '').replace(/(["\\])/g, '\\$1'); }
